import '.d{display:flex;}.ssr-plugin'
import '.l{width:100%;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.r{align-items:center;}.ssr-plugin'
import '.dk{text-align:center;}.ssr-plugin'
import '.dr{padding:2rem;}.ssr-plugin'
import '.gi{transition:transform 0.2s ease, opacity 0.2s ease;}.ssr-plugin'
import '.ar{margin-bottom:2rem;}.ssr-plugin'
import '.ab{justify-content:center;}.ssr-plugin'
import '.at{overflow:hidden;}.ssr-plugin'
import '.gj{margin-top:0.5rem;}.ssr-plugin'
import '.f3{background:none;}.ssr-plugin'
import '.be{border:none;}.ssr-plugin'
import '.z{cursor:pointer;}.ssr-plugin'
import '.a3{color:var(--brand-secondary);}.ssr-plugin'
import '.gk{text-decoration:underline;}.ssr-plugin'
import '.f4{font-weight:500;}.ssr-plugin'
import '.p{padding:0px;}.ssr-plugin'
import '.gl{font-size:inherit;}.ssr-plugin'
import '.ao{max-width:100%;}.ssr-plugin'
import '.ag{flex-shrink:0;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { Text } from '@app/components/core/typography/Text'
import { Title } from '@app/components/core/typography/Title'
import { TextLink } from '@app/components/core/link/TextLink'
import { Button, type ButtonVariant } from '@app/components/core/button/Button'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useState, useEffect } from 'react'
import { useLocation } from 'wouter'
import { Image } from '@app/components/core/image/Image'

export interface CtaCardProps {
  title: string
  subtitle?: string
  illustration?: string | string[]
  illustrationColors?: string | string[]
  linkText?: string
  linkHref?: string
  linkOnClick?: () => void
  linkAsButton?: boolean
  buttonVariant?: ButtonVariant
  border?: boolean
  style?: Style
  onClick?: () => void
}

export const CtaCard = ({
  title,
  subtitle,
  illustration,
  illustrationColors,
  linkText,
  linkHref,
  linkOnClick,
  linkAsButton = false,
  buttonVariant = 'primary',
  border = false,
  style,
  onClick,
}: CtaCardProps) => {
  const [, setLocation] = useLocation()
  const illustrations = Array.isArray(illustration)
    ? illustration
    : illustration
      ? [illustration]
      : []
  const colors = Array.isArray(illustrationColors)
    ? illustrationColors
    : illustrationColors
      ? [illustrationColors]
      : []
  const hasMultipleIllustrations = illustrations.length > 1
  return (
    <styled.div className="d l e r dk dr gi"
      data-testid="cta-card"
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        border: border ? 'var(--border-default)' : 'none',
        borderRadius: border ? '20px' : '0',
        '&:hover': onClick
          ? {
              transform: 'translateY(-2px)',
              opacity: 0.9,
            }
          : {},
        ...style,
      }}
    >
      {/* Illustrations */}
      {illustrations.length > 0 && (
        <div className="ar d r ab l at"
        >
          {hasMultipleIllustrations ? (
            <Row
              gap="1.5rem"
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '100%',
              }}
            >
              {illustrations.map((img, index) => (
                <Illustration
                  key={index}
                  src={img}
                  alt={`${title} illustration ${index + 1}`}
                  color={colors[index]}
                  width="5rem"
                  height="5rem"
                />
              ))}
            </Row>
          ) : (
            <Illustration
              src={illustrations[0]}
              alt={`${title} illustration`}
              color={colors[0]}
              width="200px"
              height="200px"
            />
          )}
        </div>
      )}

      {/* Content */}
      <Column
        alignItems="center"
        gap="1rem"
        style={{
          maxWidth: '400px',
        }}
      >
        {/* Title */}
        <Title.H3
          style={{
            color: 'var(--brand-secondary)',
            margin: 0,
            fontSize: '1.75rem',
            fontWeight: '700',
            textAlign: 'center',
          }}
        >
          {title}
        </Title.H3>

        {/* Subtitle */}
        {subtitle && (
          <Text.Medium
            style={{
              color: 'var(--brand-secondary)',
              margin: 0,
              lineHeight: '1.6',
              textAlign: 'center',
              opacity: 0.8,
            }}
          >
            {subtitle}
          </Text.Medium>
        )}

        {/* Link or Button */}
        {linkText && (linkHref || linkOnClick) && (
          <div className="gj">
            {linkAsButton ? (
              <Button
                variant={buttonVariant}
                label={linkText}
                onClick={
                  linkOnClick || (() => linkHref && setLocation(linkHref))
                }
              />
            ) : linkHref ? (
              <TextLink to={linkHref} icon={faArrowRight}>
                {linkText}
              </TextLink>
            ) : (
              <button className="f3 be z a3 gk f4 p gl"
                onClick={linkOnClick}
              >
                {linkText}
              </button>
            )}
          </div>
        )}
      </Column>
    </styled.div>
  )
}

// Illustration component that handles SVG coloring
interface IllustrationProps {
  src: string
  alt: string
  color?: string
  width: string
  height: string
}

const Illustration = ({
  src,
  alt,
  color,
  width,
  height,
}: IllustrationProps) => {
  const [svgContent, setSvgContent] = useState<string | null>(null)
  const isSvg = src.endsWith('.svg')

  useEffect(() => {
    // Only fetch SVG content if we need to apply color
    if (isSvg && color) {
      fetch(src)
        .then((response) => response.text())
        .then((svgText) => {
          // Apply color to the SVG by replacing fill attributes or adding a style
          const coloredSvg = svgText
            .replace(/<svg/, `<svg style="width: 100%; height: 100%;"`)
            .replace(/fill="[^"]*"/g, `fill="${color}"`)
            .replace(/<path/g, `<path fill="${color}"`)
            .replace(/<circle/g, `<circle fill="${color}"`)
            .replace(/<rect/g, `<rect fill="${color}"`)
            .replace(/<polygon/g, `<polygon fill="${color}"`)
          setSvgContent(coloredSvg)
        })
        .catch(() => {
          // Fallback to regular img if fetch fails
          setSvgContent(null)
        })
    }
  }, [src, color, isSvg])

  // If SVG with color and we have the content, render inline
  if (isSvg && color && svgContent) {
    return (
      <styled.div className="ao d r ab"
        style={{
          width,
          height,}}
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />
    )
  }

  // Default: render as img tag
  return (
    <styled.div className="ao d r ab ag"
      style={{
        width,
        height,}}
    >
      <Image
        src={src}
        alt={alt}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          maxWidth: '100%',
          maxHeight: '100%',
        }}
      />
    </styled.div>
  )
}
